/* CHI-20: EventDetail "My Ticket(s)" tab panel + registration-card CTA. Reuses TicketQrCard. */
import { BuyTicketsCta } from "./buyTicketsCta";
// CompleteTicketPurchase (temporary)
import { CompleteTicketPurchaseButton } from "@/components/CompleteTicketPurchase";
import { TicketGroupHeader, TicketQrCard } from "./MyTickets";
import { useServerNow } from "@/lib/serverClock";
import { SUPPORT_EMAIL } from "@/lib/ticketingApi";
import { ORDER_UNDER_REVIEW_COPY } from "@/lib/ticketRegistrationCard";
import type { MyTicket } from "@/lib/ticketingApi";
import type { TicketCardState } from "@/lib/ticketRegistrationCard";
import { formatCents, StruckPrice } from "./TicketPurchaseParts";

export function EventTicketsPanel({ tickets, eventTitle, details, serverNow, onGuestUpdated }: { tickets: MyTicket[]; eventTitle?: string; details?: { date?: string; time?: string; venue?: string }; serverNow?: string; onGuestUpdated: () => void }) {
  const now = useServerNow(serverNow, 30_000);
  return (
    <section className="chi-ticket-group" aria-label="Tickets for this event">
      <TicketGroupHeader tickets={tickets} eventTitle={eventTitle} details={details} />
      <ul style={{ display: "grid", gap: "0.75rem", padding: 0, margin: 0 }}>
        {tickets.map((t, index) => (
          <li key={t.ticketId} style={{ listStyle: "none", minWidth: 0 }}>
            <TicketQrCard ticket={t} eventTitle={eventTitle} details={details} now={now} onGuestUpdated={onGuestUpdated} includeStyles={index === 0} />
          </li>
        ))}
      </ul>
    </section>
  );
}

const btn = { width: "100%", padding: "0.9rem", borderRadius: "0.625rem", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "0.875rem", letterSpacing: "0.06em", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" } as const;

/** `buyTicketsSlug` set = signed-out viewer Dev1's rule allows: the ONE CTA is Dev1's Buy Tickets link. */
export function TicketRegistrationCta({ state, accentColor, onGetTickets, onViewTickets, buyTicketsSlug, completePurchase = false }: { state: Exclude<TicketCardState, { kind: "legacy" }>; accentColor: string; onGetTickets: () => void; onViewTickets: () => void; buyTicketsSlug?: string | null; /** CompleteTicketPurchase (temporary) */ completePurchase?: boolean }) {
  if (state.kind === "price") {
    return (
      <div data-testid="ticket-card-state" data-state="price">
        {state.unitAmountCents != null && (
          <p style={{ color: "white", marginBottom: "0.75rem" }}>
            <strong>{formatCents(state.unitAmountCents, state.currency)}</strong>{" "}
            {state.fullAmountCents != null && <StruckPrice cents={state.fullAmountCents} currency={state.currency} />}
          </p>
        )}
        {buyTicketsSlug && state.available ? (completePurchase ? <CompleteTicketPurchaseButton slug={buyTicketsSlug} /> : <BuyTicketsCta slug={buyTicketsSlug} />) : <button type="button" onClick={onGetTickets} disabled={!state.available} aria-disabled={!state.available} style={{ ...btn, border: "none", color: "#080C18", background: state.available ? `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` : "rgba(148,163,184,0.10)", cursor: state.available ? "pointer" : "not-allowed" }}>
          {state.available ? "Get tickets" : "Tickets unavailable"}
        </button>}
        {!state.available && <p style={{ margin: ".75rem 0 0", color: "#94A3B8" }}>Internal checkout is not yet available for this event. Please check back for registration details.</p>}
      </div>
    );
  }
  if (state.kind === "pending_approval") {
    return (
      <div data-testid="ticket-card-state" data-state="pending_approval">
        <button type="button" disabled aria-disabled style={{ ...btn, border: "1px solid rgba(148,163,184,0.22)", background: "rgba(148,163,184,0.10)", color: "#CBD5E1", cursor: "not-allowed" }}>Awaiting Approval</button>
        <p role="status" style={{ marginTop: ".75rem", color: "#FCD34D" }}>Your registration is awaiting approval. The event link will appear here once it is approved.</p>
      </div>
    );
  }
  if (state.kind === "under_review") {
    // CHI-22 follow-up: neutral, non-actionable. No button, no My Ticket(s) link, no price CTA.
    return (
      <div data-testid="ticket-card-state" data-state="under_review" role="status">
        <p style={{ color: "white", fontWeight: 700 }}>{ORDER_UNDER_REVIEW_COPY}</p>
        {SUPPORT_EMAIL ? <p style={{ color: "#94A3B8" }}>Questions? Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p> : null}
      </div>
    );
  }
  if (state.kind === "finalizing") {
    return (
      <div data-testid="ticket-card-state" data-state="finalizing" role="status" aria-live="polite">
        <p style={{ color: "white", fontWeight: 700 }}>Finalizing your tickets</p>
        <p>Payment confirmed. We're preparing your tickets and QR codes. We'll also email your ticket details.</p>
      </div>
    );
  }
  return (
    <div data-testid="ticket-card-state" data-state="registered">
      <p style={{ color: "#4ADE80", fontWeight: 700 }}>You're Registered!</p>
      <button type="button" onClick={onViewTickets} style={{ ...btn, background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)", color: "#4ADE80" }}>My Tickets</button>
    </div>
  );
}
